import { validateLength } from '../../utils/validators';

export const parseTime = (value: string | undefined) => {
    if (!value) {
        return undefined;
    }

    value = value.replace(':', '').padStart(4, '0');

    return `${value.slice(0, 2)}:${value.slice(2)}`;
};

const lengths = {
    name: {
        min: 1,
        max: 64,
    },
    seatNumber: {
        min: 1,
        max: 10,
    },
    cabin: {
        min: 0,
        max: 1,
    },
    tier: {
        min: 0,
        max: 50,
    },
    flight: {
        min: 1,
        max: 8,
    },
    transitPoints: {
        min: 0,
        max: 64,
    },
    notes: {
        min: 0,
        max: 2000,
    },
} as const;

const validateAirportCode = (value: string | undefined, required = true) => {
    if (value === undefined || value === '') {
        return !required;
    }

    const regex = /^[A-Z]{3}$/;
    return regex.test(value);
};

const validateDate = (value: string | undefined) => {
    if (!value) {
        return true;
    }

    return /^(\d{4}-\d{2}-\d{2})$/.test(value) && !isNaN(Date.parse(value));
};

const validateTime = (value: string | undefined, required = true) => {
    const parsed = parseTime(value);

    if (!parsed) {
        return !required;
    }

    return /^([01]\d|2[0-3]):[0-5]\d$/.test(parsed);
};

const customerKeys = [
    'firstName',
    'surname',
    'passengerCount',
    'seatNumber',
    'cabin',
    'tier',
    'flightIata',
    'departureAirport',
    'destinationAirport',
    'transitPoint',
    'departureDate',
    'departureTime',
    'arrivalDate',
    'arrivalTime',
    'overnightStay',
    'notes',
] as const;

type CustomerKey = (typeof customerKeys)[number];

type Validators = {
    [K in CustomerKey]: (value?: string) => boolean;
};

const validators: Validators = {
    firstName: (value) => {
        return validateLength(value, lengths.name);
    },
    surname: (value) => {
        return validateLength(value, lengths.name);
    },
    passengerCount: (value) => {
        if (!value) {
            return true;
        }

        const parsed = Number(value);
        return !isNaN(parsed) && parsed > 0;
    },
    seatNumber: (value) => {
        return validateLength(value, lengths.seatNumber);
    },
    cabin: (value) => {
        return validateLength(value, lengths.cabin, false);
    },
    tier: (value) => {
        return validateLength(value, lengths.tier, false);
    },
    flightIata: (value) => {
        return validateLength(value, lengths.flight);
    },
    departureAirport: (value) => {
        return validateAirportCode(value, false);
    },
    destinationAirport: (value) => {
        return validateAirportCode(value);
    },
    transitPoint: (value) => {
        return validateLength(value, lengths.transitPoints, false);
    },
    departureDate: (value) => {
        return validateDate(value);
    },
    departureTime: (value) => {
        return validateTime(value);
    },
    arrivalDate: (value) => {
        return validateDate(value);
    },
    arrivalTime: (value) => {
        return validateTime(value, false);
    },
    overnightStay: (value) => {
        if (!value) {
            return true;
        }

        if (value.length > 1) {
            return false;
        }

        const uppercase = value.toUpperCase();
        return uppercase === 'Y' || uppercase === 'N';
    },
    notes: (value) => {
        return validateLength(value, lengths.notes, false);
    },
} as const;

export type MisconnectedCustomerInput = {
    [K in CustomerKey]: string;
};

export const isEmptyCustomer = (
    customer: MisconnectedCustomerInput
): boolean => {
    return Object.values(customer).every((value) => value.trim() === '');
};

export const validateCustomer = (
    customer: MisconnectedCustomerInput
): boolean => {
    for (const key of customerKeys) {
        const validator = validators[key];
        const valid = validator(customer[key]);
        if (!valid) {
            return false;
        }
    }

    return true;
};
